import { getDomRoot } from './dom-root.util';

/**
 * Where keyboard focus goes when a booking that was locked for a payment unlocks (the payment was
 * cancelled, its window closed without paying, it was refused, or it succeeded before the page moved
 * on). The control that held focus - "Cancel payment", the disabled pay button - is gone or disabled
 * by then, so focus fell to the page and a keyboard user would start again from its top.
 *
 * In order, the first one that can take focus: the payment's own pay button (the hosted checkout's
 * "Pay with card"), the host's Book button, the payment pane heading, and the pane itself.
 *
 * Focus is only moved when it is free (on the page body) or still inside the widget: a shopper who
 * has meanwhile moved on to the embedding page keeps their place there.
 *
 * @param pane the payment pane (cw-payment-methods' body), which must carry tabindex="-1"
 * @returns the element focused, or null when focus was left where it is
 */
export function focusAfterPaymentLock(pane: HTMLElement): HTMLElement | null {
  const root = getDomRoot(pane);
  if (!isFocusFree(pane, root)) {
    return null;
  }
  const target = firstFocusable(pane, '.cw-adyen__hosted-pay')
    ?? firstFocusable(root, 'button#cbw-booking-finish')
    ?? firstFocusable(pane, '.cw-payment-pane-heading')
    ?? pane;
  target.focus();

  return target;
}

function isFocusFree(pane: HTMLElement, root: Document | ShadowRoot): boolean {
  const active = document.activeElement;
  if (!active || active === document.body) {
    return true;
  }
  if (typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) {
    return active === root.host;
  }

  return !!widgetElement(pane)?.contains(active);
}

/**
 * The widget's own outermost element in a light-DOM page: the outermost custom element around the
 * pane (`<calenso-booking>`, or its brand's tag). Null when the pane sits in no custom element.
 */
function widgetElement(pane: HTMLElement): Element | null {
  let outermost: Element | null = null;
  for (let element = pane.parentElement; element; element = element.parentElement) {
    if (element.localName.includes('-')) {
      outermost = element;
    }
  }

  return outermost;
}

function firstFocusable(scope: ParentNode, selector: string): HTMLElement | null {
  return Array.from(scope.querySelectorAll<HTMLElement>(selector)).find(element =>
    !(element as HTMLButtonElement).disabled
    && !element.closest('[inert]')
    && element.getClientRects().length > 0
  ) ?? null;
}
